import React from 'react';
import { Box3, Vector3 } from 'three';

import { Component, Member } from '@paragon/contract-library';
import { angleEquals, Direction2D } from '@paragon/geometry-library';
import { calculateMemberDirectionedMidline, calculateMemberThreeJsGeometry } from '@paragon/three-shared';
import { notNullish } from '@paragon/utilities';

import { useAppSelector } from '../../components/hooks';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getInsertingObjectInfo } from '../../store/selectors/insertionObjectSelectors';
import { SlopeLabel } from './SlopeLabel';

const chordSlopeLabelOffset = 18;
const chordSlopeLabelOffsetForSlightSlope = 12;
const slightSlopeThreshold = 10;

function getMembersSortedLeftToRight(members: readonly Member[]) {
  return members.toSorted((a, b) => {
    const aMinX = Math.min(...a.geometry.map((point) => point.x));
    const bMinX = Math.min(...b.geometry.map((point) => point.x));
    if (aMinX > bMinX) return 1;
    if (bMinX > aMinX) return -1;
    return 0;
  });
}

function buildSlopeLabelsForChords(chords: readonly Member[], component: Component, slopeOffsetSign: number) {
  if (chords.length === 0) return [];
  const sortedChords = getMembersSortedLeftToRight(chords);
  const slopeLabels: { key: string; angle: number; position: Vector3 }[] = [];
  let currentChordRun: Member[] = [];
  let currentRunDirection: Direction2D | null =
    calculateMemberDirectionedMidline(sortedChords[0], component)?.direction ?? null;
  // Group all the chords into runs, processing the previous run when a new one starts. When all the chords are gone,
  // process the last run.
  while (sortedChords.length > 0 || currentChordRun.length > 0) {
    const currentChord = sortedChords.shift();

    // The current chord direction helps us distinguish chords whose angles are the same but one is negative slope
    const currentChordDirection = currentChord
      ? (calculateMemberDirectionedMidline(currentChord, component)?.direction ?? null)
      : null;

    // Add to the current chords array as long as the slopes (and their sign, as discerned above) match
    if (
      currentChord &&
      (currentChordRun.length === 0 || angleEquals(currentChord.angle, currentChordRun[0].angle)) &&
      (currentRunDirection
        ? currentChordDirection?.equals(currentRunDirection) ||
          currentChordDirection?.reverse().equals(currentRunDirection)
        : true)
    ) {
      currentChordRun.push(currentChord);
      continue;
    }

    // Build a slope label for the current run if the run is not horizontal or vertical
    const currentRunAngle = currentChordRun[0].angle;
    if (!angleEquals(currentRunAngle, 0) && !angleEquals(currentRunAngle, 90)) {
      const geometryPoints = currentChordRun
        .map((member) => calculateMemberThreeJsGeometry(member, component))
        .filter(notNullish)
        .flatMap((geometry) => [geometry.boundingBox!.min, geometry.boundingBox!.max]);
      const centroid = new Vector3();
      new Box3().setFromPoints(geometryPoints).getCenter(centroid);
      const offSetVector = new Vector3().setY(
        currentRunAngle < slightSlopeThreshold
          ? chordSlopeLabelOffsetForSlightSlope * slopeOffsetSign
          : chordSlopeLabelOffset * slopeOffsetSign,
      );
      slopeLabels.push({
        key: currentChordRun.map((member) => member.guid).join('-'),
        angle: currentRunAngle,
        position: centroid.add(offSetVector),
      });
    }

    // Reset the current run of chords to the current chord with its different slope (unless we are at the end)
    if (currentChord) {
      currentChordRun = [currentChord];
      currentRunDirection = currentChordDirection;
    } else currentChordRun = [];
  }

  return slopeLabels;
}

export function ChordSlopes() {
  const insertingObjectInfo = useAppSelector(getInsertingObjectInfo);
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);
  const showPitch = useAppSelector((state) => state.visualOptions.showPitch);

  if (!showPitch || !activeComponentDesign || insertingObjectInfo?.type === 'componentDesign') return null;

  const { component } = activeComponentDesign;

  const topChords = component.members.filter((member) => member.memberType === 'TopChord');
  const topChordSlopeLabels = buildSlopeLabelsForChords(topChords, component, 1);
  const bottomChords = component.members.filter((member) => member.memberType === 'BottomChord');
  const bottomChordSlopeLabels = buildSlopeLabelsForChords(bottomChords, component, -1);
  const slopeLabels = topChordSlopeLabels.concat(bottomChordSlopeLabels);

  return (
    <>
      {slopeLabels.map((slopeLabel) => (
        <SlopeLabel degrees={slopeLabel.angle} position={slopeLabel.position} key={slopeLabel.key} />
      ))}
    </>
  );
}
